import React, { useState } from 'react'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { Upload, X } from 'lucide-react'

const MAX_FILE_SIZE_MB = 1

type Props = {
    label?: string
    name: string
    control: Control<any, any>
    errors: FieldErrors<any>
    preview?: string | null
    allowedTypes?: string[]
    tooltip?: {
        title?: string
        items: string[]
        position?: 'top' | 'bottom' | 'left' | 'right'
    }
    rules?: any
    required?: boolean
    fileSizeLimit?: { min_size: number; max_size: number; message: string }
}

const FileInputUploadFieldNew: React.FC<Props> = ({
    label,
    name,
    control,
    errors,
    preview,
    allowedTypes = [],
    tooltip,
    rules,
    required,
    fileSizeLimit,
}) => {
    const [localError, setLocalError] = useState<string | null>(null)

    const tooltipPositionClass = {
        top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
        left: 'right-full top-1/2 -translate-y-1/2 mr-2',
        right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={{
                ...rules,
                validate: {
                    fileType: (value: File | string | null) => {
                        // ✅ existing file URL → always valid
                        if (!value || typeof value === 'string') return true

                        return (
                            allowedTypes.length === 0 ||
                            allowedTypes.includes(value.type) ||
                            'Invalid file type'
                        )
                    },
                    fileSize: (value: File | string | null) => {
                        if (!value || typeof value === 'string') return true

                        if (fileSizeLimit) {
                            const minBytes = fileSizeLimit.min_size * 1024
                            const maxBytes = fileSizeLimit.max_size * 1024

                            return (
                                (value.size >= minBytes && value.size <= maxBytes) ||
                                fileSizeLimit.message
                            )
                        }

                        return (
                            value.size / (1024 * 1024) <= MAX_FILE_SIZE_MB ||
                            `File size must be ≤ ${MAX_FILE_SIZE_MB}MB`
                        )
                    }

                },
            }}
            render={({ field, fieldState }) => (
                <div>
                    {/* Label */}
                    <div className="flex items-center gap-1 mb-2">
                        <label
                            className={`text-xs font-medium dark:text-gray-300

                            `}
                        // ${
                        //     fieldState.error
                        //         ? 'text-red-600'
                        //         : 'text-gray-700'
                        // }
                        >
                            {label}
                        </label>
                        {required && <span className="text-red-700">*</span>}
                        {tooltip && (
                            <div className="relative group">
                                <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer inline" />
                                <div
                                    className={`absolute ${tooltipPositionClass[
                                        tooltip.position ?? 'top'
                                        ]
                                        } w-64 rounded-md z-50 bg-white border p-3 text-xs shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 transition-opacity`}
                                >
                                    {tooltip.title && (
                                        <p className="font-semibold mb-1">
                                            {tooltip.title}
                                        </p>
                                    )}
                                    <ul className="space-y-1">
                                        {tooltip.items.map((item, idx) => (
                                            <li key={idx}>• {item}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Upload box */}
                    <label
                        htmlFor={name}
                        className="flex justify-center items-center gap-4 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:border-blue-400 transition pointer-events-auto"
                    >
                        {preview && typeof preview === 'string' ? (
                            <div className="relative pointer-events-none">
                                <button
                                    type="button"
                                    className="pointer-events-auto absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        field.onChange(null)
                                        const input = document.getElementById(
                                            name
                                        ) as HTMLInputElement
                                        if (input) input.value = ''
                                    }}
                                >
                                    <X size={14} className="text-red-500" />
                                </button>

                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="w-22 h-12 object-cover rounded"
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <Upload
                                    className="text-gray-400 mb-2"
                                    size={20}
                                />
                                <p className="text-sm text-gray-500">
                                    Click to upload
                                </p>
                            </div>
                        )}
                    </label>

                    {/* Hidden input */}
                    <input
                        id={name}
                        type="file"
                        className="hidden"
                        accept={allowedTypes.join(',')}
                        onChange={(e) => {
                            setLocalError(null)
                            const file = e.target.files?.[0] ?? null
                            field.onChange(file) // ✅ RHF now fully aware
                        }}
                    />

                    {/* Error */}
                    {(fieldState.error || localError) && (
                        <p className="text-red-500 text-xs mt-1">
                            {fieldState.error?.message || localError}
                        </p>
                    )}
                </div>
            )}
        />
    )
}

export default FileInputUploadFieldNew
